// Auth Service - Handles authentication API calls
import api from './api';

export interface User {
  id: number;
  email?: string;
  studentId?: string;
  firstName: string;
  lastName: string;
  role: 'student' | 'instructor' | 'admin' | 'global_admin';
  authProvider?: 'local' | 'google' | 'microsoft';
}

export interface LoginResponse {
  success: boolean;
  token: string;
  user: User;
}

export interface StudentLoginData {
  coursePassword: string;
  studentId?: string;
  firstName?: string;
  lastName?: string;
}

export interface InstructorLoginData {
  email?: string;
  password?: string;
  coursePassword?: string;
}

class AuthService {
  // Unified email/password login
  async login(data: { email: string; password: string }): Promise<LoginResponse | null> {
    const result = await api.post<LoginResponse>('/auth/login', data);

    if (result.success && result.data) {
      api.setToken(result.data.token);
      return result.data;
    }

    return null;
  }

  // Unified registration
  async register(data: { email: string; password: string; firstName: string; lastName: string }): Promise<{ success: boolean; message?: string; error?: string }> {
    const result = await api.post<{ success: boolean; message?: string }>('/auth/register', data);
    return result.data || { success: false, error: 'Registration failed' };
  }

  // Handle OAuth callback (token from Google redirect)
  async handleOAuthCallback(token: string): Promise<User | null> {
    api.setToken(token);
    return this.verifyToken();
  }

  // Legacy: Student login
  async studentLogin(data: StudentLoginData): Promise<LoginResponse | null> {
    const result = await api.post<LoginResponse>('/auth/student/login', data);
    if (result.success && result.data) {
      api.setToken(result.data.token);
      return result.data;
    }
    return null;
  }

  // Legacy: Instructor login
  async instructorLogin(data: InstructorLoginData): Promise<LoginResponse | null> {
    const result = await api.post<LoginResponse>('/auth/instructor/login', data);
    if (result.success && result.data) {
      api.setToken(result.data.token);
      return result.data;
    }
    return null;
  }

  async verifyToken(): Promise<User | null> {
    const result = await api.get<{ success: boolean; user: User }>('/auth/verify');

    if (result.success && result.data?.user) {
      return result.data.user;
    }

    return null;
  }

  async logout(): Promise<void> {
    await api.post('/auth/logout');
    api.setToken(null);
  }

  getStoredToken(): string | null {
    return api.getToken();
  }
}

export const authService = new AuthService();
export default authService;
